import React from 'react'
import Section from '../../common/section/section'
import { MembershipBilling } from 'memberships/schema'
import { DateFormats, formatISOString } from 'utils/dates'
import InfoRow, { InfoRowContainer } from 'uiComponents/infoRow'
import { parse, addMonths } from 'date-fns'
import Badge from 'uiComponents/badge/badge'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useTranslation } from 'translations'
import './billing.scss'

interface BillingSectionProps {
    billing?: MembershipBilling
    isLoading?: boolean
}

const BillingSection = ({ billing, isLoading }: BillingSectionProps) => {
    const { t } = useTranslation()
    let paymentMethodText, expirationDateValue, paymentScheduleText, nextPaymentText

    if (billing) {
        paymentMethodText =
            billing.paymentMethod === 'creditcard'
                ? t('memberships.membershipDetailsPanel.billing.creditCard')
                : billing.paymentMethod === 'ideal'
                ? 'iDEAL'
                : billing.paymentMethod

        if (billing.expirationDate) {
            const expirationDate = parse(billing.expirationDate, 'MM/yy', new Date())
            const isCardExpired = new Date() > addMonths(expirationDate, 1)
            expirationDateValue = (
                <>
                    {formatISOString(expirationDate, 'MM/yyyy')}
                    {isCardExpired && (
                        <Badge variant="error">
                            <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />
                            {t('memberships.membershipDetailsPanel.billing.expired')}
                        </Badge>
                    )}
                </>
            )
        }

        paymentScheduleText =
            billing.paymentSchedule === 'P1Y'
                ? t('memberships.membershipDetailsPanel.billing.yearly')
                : billing.paymentSchedule === 'P1M'
                ? t('memberships.membershipDetailsPanel.billing.monthly')
                : undefined

        if (billing.nextPayment) {
            nextPaymentText = formatISOString(billing.nextPayment, DateFormats.EXTRA_LONG_DATE)
        }
    }

    return (
        <Section className="billing" title={t('memberships.membershipDetailsPanel.billing.title')}>
            <InfoRowContainer loading={isLoading}>
                <InfoRow label={t('memberships.membershipDetailsPanel.billing.linkedEmail')} className="linked-email" value={billing?.linkedEmail} />
                <InfoRow label={t('memberships.membershipDetailsPanel.billing.paymentMethod')} className="payment-method" value={paymentMethodText} />
                {billing?.paymentMethod === 'creditcard' || !billing?.paymentMethod ? (
                    <>
                        <InfoRow label={t('memberships.membershipDetailsPanel.billing.cardNumber')} className="card-number" value={billing?.paymentMethodDetails} />
                        <InfoRow
                            label={t('memberships.membershipDetailsPanel.billing.cardExpirationDate')}
                            className="card-expiration-date"
                            value={expirationDateValue}
                        />
                    </>
                ) : (
                    <InfoRow label={t('memberships.membershipDetailsPanel.billing.accountNumber')} className="account-number" value={billing?.paymentMethodDetails} />
                )}
                <InfoRow label={t('memberships.membershipDetailsPanel.billing.paymentSchedule')} className="payment-schedule" value={paymentScheduleText} />
                <InfoRow label={t('memberships.membershipDetailsPanel.billing.nextPayment')} className="next-payment" value={nextPaymentText} />
            </InfoRowContainer>
        </Section>
    )
}
export default BillingSection
